// ---------------------------------------------------------------------------
// Hip-spine angle adapters that produce scalar measurements from approved-
// landmark API DTOs. Used by the classification / nomogram pipeline, where
// consumers need plain numbers to feed into business rules (flatback, stiff,
// PPT) rather than the renderable overlay objects produced by
// `hipSpineAngleOverlays.ts`. The actual geometric formulas live in
// `hipSpineAngleFormulas.ts`; this file is a thin adapter that resolves named
// landmarks from a DTO and forwards to those primitives.
// ---------------------------------------------------------------------------

import type {
    HipSpineApprovedLandmarkApiObject,
    HipSpineLandmark,
} from '@/api/hip-spine-study/hipSpineLandmark';
import {
    HipSpineLandmarkName,
    LUMBAR_LEVEL_PAIRS,
    type LumbarLevel,
} from '@/domain/anatomy/hipSpineLandmarks';
import {
    appTilt,
    lumbarLordosis,
    pelvicFemoralAngle,
    pelvicIncidence,
    sacralSlope,
} from '@/domain/anatomy/hipSpineAngleFormulas';
import { Vector2 } from 'three';

/**
 * Thrown when an angle cannot be computed because one or more required
 * landmarks are absent from the approved-landmark DTO. The viewmodel catches
 * this at its boundary and degrades to a hidden panel; the message string
 * names the angle and missing landmarks for stack traces and error reporting.
 */
export class MissingLandmarksError extends Error {
    constructor(angleName: string, missing: readonly string[]) {
        super(`Cannot compute ${angleName}: missing landmark(s) ${missing.join(', ')}`);
        this.name = 'MissingLandmarksError';
    }
}

function requireLandmarkVectors(
    angleName: string,
    landmarks: readonly HipSpineLandmark[],
    names: readonly string[],
): Vector2[] {
    const resolved = names.map((n) => landmarks.find((l) => l.name === n) ?? null);
    const missing = names.filter((_, i) => resolved[i] === null);
    if (missing.length > 0) throw new MissingLandmarksError(angleName, missing);
    return (resolved as HipSpineLandmark[]).map((l) => new Vector2(l.x, l.y));
}

/**
 * Compute the Anterior Pelvic Plane (APP) angle in degrees on a lateral
 * sagittal X-ray. Magnitude is the acute angle between the pubic-symphysis →
 * ASIS-midpoint line and the y-axis.
 *
 * Positive = anterior pelvic tilt; negative = posterior pelvic tilt (PPT).
 * Anterior direction is derived from the sacrum landmarks.
 *
 * Throws {@link MissingLandmarksError} when any of asis_1, asis_2,
 * pubic_symphysis, sacrum_anterior, sacrum_posterior is absent.
 */
export function computeAPPAngle(landmarks: HipSpineApprovedLandmarkApiObject): number {
    const [asis1, asis2, pubicSymphysis, sacrumAnterior, sacrumPosterior] =
        requireLandmarkVectors('APP angle', landmarks.landmarks, [
            HipSpineLandmarkName.ASIS1,
            HipSpineLandmarkName.ASIS2,
            HipSpineLandmarkName.PubicSymphysis,
            HipSpineLandmarkName.SacrumAnterior,
            HipSpineLandmarkName.SacrumPosterior,
        ]);
    return appTilt(
        { a: asis1, b: asis2 },
        pubicSymphysis,
        sacrumAnterior,
        sacrumPosterior,
    );
}

/**
 * Pelvic incidence in degrees from approved landmarks. Signed: negative when
 * the sacrum points posteriorly (see {@link pelvicIncidence}). Classification
 * consumes the same signed value the overlay displays. Throws
 * {@link MissingLandmarksError} when any of sacrum_anterior, sacrum_posterior,
 * HJC_1, HJC_2 is absent.
 */
export function computePelvicIncidence(
    approved: HipSpineApprovedLandmarkApiObject,
): number {
    const [sacrumAnterior, sacrumPosterior, hjc1, hjc2] = requireLandmarkVectors(
        'pelvic incidence',
        approved.landmarks,
        [
            HipSpineLandmarkName.SacrumAnterior,
            HipSpineLandmarkName.SacrumPosterior,
            HipSpineLandmarkName.HJC1,
            HipSpineLandmarkName.HJC2,
        ],
    );
    return pelvicIncidence({ a: sacrumAnterior, b: sacrumPosterior }, hjc1, hjc2);
}

/**
 * Lumbar lordosis in degrees from approved landmarks, using the endplate pair at
 * the specified lumbar `level`. Signed: positive when lordotic (endplates open
 * anterior), negative when kyphotic (see {@link lumbarLordosis}). Classification
 * consumes the same signed value the overlay displays. Throws
 * {@link MissingLandmarksError} when the sacrum pair or the level's endplate pair
 * is absent.
 */
export function computeLumbarLordosisAtLevel(
    approved: HipSpineApprovedLandmarkApiObject,
    level: LumbarLevel,
): number {
    const [sacrumAnterior, sacrumPosterior] = requireLandmarkVectors(
        'lumbar lordosis',
        approved.landmarks,
        [HipSpineLandmarkName.SacrumAnterior, HipSpineLandmarkName.SacrumPosterior],
    );

    const pair = LUMBAR_LEVEL_PAIRS.find((candidate) => candidate.level === level);
    if (!pair) {
        // Unreachable unless the LumbarLevel union diverges from LUMBAR_LEVEL_PAIRS.
        // Throw a plain Error (not MissingLandmarksError) so a try* caller surfaces
        // this programmer error rather than silently returning null.
        throw new Error(`computeLumbarLordosisAtLevel: unrecognised level '${level}'`);
    }

    const [lumbarAnterior, lumbarPosterior] = requireLandmarkVectors(
        'lumbar lordosis',
        approved.landmarks,
        [pair.anterior, pair.posterior],
    );

    return lumbarLordosis(
        { a: sacrumAnterior, b: sacrumPosterior },
        { a: lumbarAnterior, b: lumbarPosterior },
    );
}

/**
 * Like {@link computeLumbarLordosisAtLevel}, but returns null when the sacrum
 * pair or the level's endplate pair is missing. Used for the optional stiffness
 * branch, where missing data degrades the stiff dimension to null rather than
 * failing the whole classification.
 */
export function tryComputeLumbarLordosisAtLevel(
    approved: HipSpineApprovedLandmarkApiObject,
    level: LumbarLevel,
): number | null {
    try {
        return computeLumbarLordosisAtLevel(approved, level);
    } catch (e) {
        if (e instanceof MissingLandmarksError) return null;
        throw e;
    }
}

/**
 * True when the approved-landmark DTO contains BOTH endplate landmarks for the
 * given lumbar `level`. Mirrors the backend `1 in posture.lumbar` guard used by
 * `select_mobility`: it tests the lumbar pair only (NOT the sacrum), so the
 * stiff-spine path selection matches the backend exactly.
 */
export function hasLumbarEndplatePair(
    approved: HipSpineApprovedLandmarkApiObject,
    level: LumbarLevel,
): boolean {
    const pair = LUMBAR_LEVEL_PAIRS.find((candidate) => candidate.level === level);
    if (!pair) return false;
    const present = new Set(approved.landmarks.map((l) => l.name));
    return present.has(pair.anterior) && present.has(pair.posterior);
}

/**
 * True when both sacral-endplate landmarks are present. Mirrors the backend's
 * load-time guarantee (`from_records` requires the sacrum pair, so a sacrum-less
 * posture is dropped before mobility selection): a posture without a sacrum can
 * contribute neither a lordosis nor a sacral-slope measurement, so the stiff-spine
 * selection must not treat it as an L1 source.
 */
export function hasSacralEndplatePair(approved: HipSpineApprovedLandmarkApiObject): boolean {
    const present = new Set(approved.landmarks.map((l) => l.name));
    return (
        present.has(HipSpineLandmarkName.SacrumAnterior) &&
        present.has(HipSpineLandmarkName.SacrumPosterior)
    );
}

/**
 * Sacral slope in degrees from approved landmarks. Signed: negative when the
 * sacrum points posteriorly, positive otherwise (see {@link sacralSlope}).
 * Throws {@link MissingLandmarksError} when either sacrum landmark is absent.
 */
export function computeSacralSlope(approved: HipSpineApprovedLandmarkApiObject): number {
    const [sacrumAnterior, sacrumPosterior] = requireLandmarkVectors(
        'sacral slope',
        approved.landmarks,
        [HipSpineLandmarkName.SacrumAnterior, HipSpineLandmarkName.SacrumPosterior],
    );
    return sacralSlope({ a: sacrumAnterior, b: sacrumPosterior });
}

/**
 * Like {@link computeSacralSlope}, but returns null when a sacrum landmark is
 * missing. Used for the optional ΔSS stiffness branch on the flexed-seated posture.
 */
export function tryComputeSacralSlope(
    approved: HipSpineApprovedLandmarkApiObject,
): number | null {
    try {
        return computeSacralSlope(approved);
    } catch (e) {
        if (e instanceof MissingLandmarksError) return null;
        throw e;
    }
}

// Sides are 1 and 2 instead of left/right because we can't tell which
// side of the body each landmark is on.
function computePelvicFemoralAngleBySide(
    landmarks: HipSpineApprovedLandmarkApiObject,
    side: 1 | 2,
): number {
    const [sacrumAnterior, sacrumPosterior, hjc, femurCortex] = requireLandmarkVectors(
        'pelvic femoral angle',
        landmarks.landmarks,
        [
            HipSpineLandmarkName.SacrumAnterior,
            HipSpineLandmarkName.SacrumPosterior,
            side === 1 ? HipSpineLandmarkName.HJC1 : HipSpineLandmarkName.HJC2,
            side === 1 ? HipSpineLandmarkName.FemurCortex1 : HipSpineLandmarkName.FemurCortex2,
        ],
    );
    return pelvicFemoralAngle(
        { a: sacrumAnterior, b: sacrumPosterior },
        hjc,
        femurCortex,
    );
}

/**
 * Compute the pelvic femoral angle as the average of the per-side angles at
 * both hip joint centres (HJC_1 and HJC_2). Each angle is measured at the HJC
 * vertex between rays HJC→sacrum-midpoint and HJC→femoral-cortex, on the side
 * that faces anterior.
 *
 * Throws {@link MissingLandmarksError} when any required landmark is absent.
 */
export function computeAveragePelvicFemoralAngle(
    landmarks: HipSpineApprovedLandmarkApiObject,
): number {
    const pfa1 = computePelvicFemoralAngleBySide(landmarks, 1);
    const pfa2 = computePelvicFemoralAngleBySide(landmarks, 2);
    return (pfa1 + pfa2) / 2;
}
